/**
 * WorkPage – Services, Projects, and Team
 *
 * Unified page with shared header. Content switches based on route:
 *   /services  → ServicesContent
 *   /projects  → ProjectsContent (with category filter)
 *   /team      → TeamContent
 */

import React, { useState, useMemo, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { motion } from 'framer-motion';

import Footer from '../components/layout/Footer';
import { ScrollReveal, StaggerContainer, StaggerItem } from '../components/ui/ScrollReveal';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetClose } from '../components/ui/sheet';
import { PROJECT_IMAGES } from '../constants/images';
import { getPlaceholder } from '../lib/imageLoader';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { servicesDetail, projects, teamSections } from '../data/mock';
import { Check, ImageOff, ChevronRight, X } from 'lucide-react';

// -----------------------------------------------------------------------------
// Constants
// -----------------------------------------------------------------------------

// Project categories = Services page categories (same order as servicesDetail; only those with projects)
const getProjectCategories = (p) => p.categories ?? (p.category ? [p.category] : []);
const projectCategorySet = new Set(projects.flatMap(getProjectCategories).filter(Boolean));
const PROJECT_CATEGORIES = [
  'All',
  ...servicesDetail.map((s) => s.title).filter((title) => projectCategorySet.has(title)),
];
const PROJECT_PLACEHOLDER = getPlaceholder(600, 400, 'Photo+coming+soon');

const WorkPage = () => {
  const location = useLocation();
  const path = location.pathname;
  const hash = location.hash;
  const [projectFilter, setProjectFilter] = useState('All');

  const isServices = path === '/services';

  // Scroll to service section when navigating with hash (e.g. from footer links)
  useEffect(() => {
    if (isServices && hash) {
      const scrollToHash = () => {
        const id = hash.replace('#', '');
        const el = document.getElementById(id);
        if (el) {
          const y = el.getBoundingClientRect().top + window.scrollY - 100;
          window.scrollTo({ top: y, behavior: 'smooth' });
        }
      };
      // Small delay to ensure content is rendered
      const t = setTimeout(scrollToHash, 100);
      return () => clearTimeout(t);
    }
  }, [isServices, hash]);
  const isProjects = path === '/projects';
  const isTeam = path === '/team';

  const getHeader = () => {
    if (isServices) return { subtitle: 'What We Do', title: 'Our Services' };
    if (isProjects) return { subtitle: 'THE WORKS', title: 'Our Projects' };
    if (isTeam) return { subtitle: 'The people behind every project', title: 'Our Team' };
    return { subtitle: '', title: '' };
  };

  const { subtitle, title } = getHeader();

  return (
    <div className="min-h-screen bg-[#FAF8F2]">
      <motion.main
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        className="pb-24 pt-24"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {/* Shared header - same spot for all three pages */}
          <div className={`flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10 ${isProjects ? '' : ''}`}>
            <div>
              <span className="text-xs tracking-[0.2em] text-[#6B6B65] uppercase">
                {subtitle}
              </span>
              <h1 className="font-serif text-4xl md:text-5xl text-[#1A1A1A] mt-2 font-semibold">
                {title}
              </h1>
            </div>
            {isProjects && (
              <Select value={projectFilter} onValueChange={setProjectFilter}>
                <SelectTrigger className="w-full sm:w-[280px] h-11 bg-[#1A1A1A] text-white border-[#1A1A1A] text-xs font-semibold tracking-widest uppercase hover:bg-[#8B7355] focus:ring-[#8B7355] [&>svg]:text-white/80">
                  <SelectValue placeholder="All categories" />
                </SelectTrigger>
                <SelectContent className="bg-[#FAF8F2] border-[#E0DDD9]">
                  {PROJECT_CATEGORIES.map((category) => (
                    <SelectItem
                      key={category}
                      value={category}
                      className="text-xs font-medium tracking-wide text-[#1A1A1A] focus:bg-[#8B7355]/10 focus:text-[#1A1A1A]"
                    >
                      {category === 'All' ? 'All categories' : category}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Content - changes based on route */}
          {isServices && <ServicesContent />}
          {isProjects && <ProjectsContent activeFilter={projectFilter} />}
          {isTeam && <TeamContent />}
        </div>
      </motion.main>

      <Footer />
    </div>
  );
};

// Services content (no header - header is above)
const ServicesContent = () => (
  <section id="services-content" className="pb-8">
    {servicesDetail.map((service, index) => (
      <ScrollReveal key={service.id} variant="fadeUp" threshold={0.15} delay={index * 0.05}>
        <div id={`service-${service.id}`}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start py-12 lg:py-16">
          <div className="lg:col-span-4 order-2 lg:order-1">
            <span className="text-6xl md:text-7xl font-light text-[#E0DDD9] leading-none block mb-4">{service.number}</span>
            <h3 className="font-serif text-2xl md:text-3xl text-[#1A1A1A] mb-6">{service.title}</h3>
            <p className="text-[#6B6B65] text-base leading-relaxed">{service.description}</p>
          </div>
          <div className="lg:col-span-5 order-1 lg:order-2">
            <img src={service.image} alt={service.title} className="w-full h-[320px] lg:h-[400px] object-cover" />
          </div>
          <div className="lg:col-span-3 order-3">
            <h4 className="font-serif text-lg font-semibold text-[#1A1A1A] mb-6">Key Point</h4>
            <ul className="space-y-4">
              {service.keyPoints.map((point, idx) => (
                <li key={idx} className="flex items-start gap-3">
                  <span className="w-5 h-5 rounded-full bg-[#1A1A1A] flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-3 h-3 text-white" />
                  </span>
                  <span className="text-[#1A1A1A] text-sm leading-relaxed">{point}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
        {index < servicesDetail.length - 1 && <hr className="border-t border-[#E0DDD9]" />}
        </div>
      </ScrollReveal>
    ))}
    <ScrollReveal variant="fadeUp" threshold={0.15}>
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center mt-20 pt-16 border-t border-[#E0DDD9]">
      <div className="grid grid-cols-2 grid-rows-3 gap-3">
        {servicesDetail.map((service) => (
          <img key={service.id} src={service.image} alt={service.title} className="w-full h-[180px] object-cover" />
        ))}
      </div>
      <div>
        <h3 className="font-serif text-3xl md:text-4xl text-[#1A1A1A] mb-6">Start Your Dream Project Today</h3>
        <p className="text-[#1A1A1A] text-base leading-relaxed mb-8">
          Whether you're envisioning a power plant erection, commissioning, pipeline works, or a major industrial renovation, our team of experienced professionals is here to guide you every step of the way. With a commitment to quality, innovation, & client satisfaction.
        </p>
        <Link to="/contact" className="inline-flex items-center gap-2 px-8 py-3 bg-[#1A1A1A] text-white text-xs font-semibold tracking-widest uppercase hover:bg-[#8B7355] transition-colors duration-300">
          Contact Us
        </Link>
      </div>
    </div>
    </ScrollReveal>
  </section>
);

// Group projects by client for summary vs single cards
const groupProjectsByClient = (projectList) => {
  const byClient = new Map();
  projectList.forEach((p) => {
    const key = p.client;
    if (!byClient.has(key)) {
      const parts = key.split(/\s*[–—]\s*/);
      byClient.set(key, {
        clientKey: key,
        clientName: parts[0]?.trim() || key,
        location: parts[1]?.trim() || '',
        years: p.results[0],
        image: p.image,
        projects: [],
      });
    }
    byClient.get(key).projects.push(p);
  });
  return Array.from(byClient.values());
};

// Unique work categories from a group's projects (e.g. ['Boiler', 'Power Plant'])
const getGroupCategories = (group) => [...new Set(group.projects.flatMap(getProjectCategories).filter(Boolean))];

// Unified client card – same style for 1 or multiple projects; opens sliding panel on click
const ClientProjectCard = ({ group, image, onOpen }) => {
  const categories = getGroupCategories(group);
  return (
    <StaggerItem key={group.clientKey}>
      <button
        type="button"
        onClick={() => onOpen(group, image)}
        className="w-full text-left group/card bg-white rounded-2xl overflow-hidden border border-[#E0DDD9] hover:border-[#8B7355]/40 hover:shadow-xl hover:shadow-[#8B7355]/10 transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#8B7355] focus-visible:ring-offset-2"
      >
        <div className="relative overflow-hidden aspect-square">
          <img
            src={image ?? group.image}
            alt={group.clientName}
            className="w-full h-full object-cover group-hover/card:scale-105 transition-transform duration-500"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A]/75 via-[#1A1A1A]/25 to-transparent" />
          {/* Category tags – work type (e.g. Power Plant | Boiler) */}
          {categories.length > 0 && (
            <div className="absolute top-3 left-3 right-3 flex flex-wrap gap-1.5">
              {categories.map((cat) => (
                <span
                  key={cat}
                  className="rounded-md bg-white/90 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-[#1A1A1A]"
                >
                  {cat}
                </span>
              ))}
            </div>
          )}
          <div className="absolute bottom-0 left-0 right-0 p-4 md:p-5 flex items-end justify-between gap-4">
            <div>
              <h3 className="font-serif text-xl md:text-2xl text-white font-semibold">{group.clientName}</h3>
              {group.location && <p className="text-white/90 text-sm mt-0.5">{group.location}</p>}
              <p className="text-white/80 text-xs font-medium tracking-wider uppercase mt-2">{group.years}</p>
            </div>
            <span className="flex shrink-0 items-center gap-1 rounded-full bg-white/20 px-3 py-1.5 text-sm font-medium text-white">
              {group.projects.length === 1 ? '1 project' : `${group.projects.length} projects`}
              <ChevronRight className="h-4 w-4" />
            </span>
          </div>
        </div>
      </button>
    </StaggerItem>
  );
};

// Projects content – filter by project category (Boiler, Power Plant, ESP, etc.)
const ProjectsContent = ({ activeFilter }) => {
  const [selected, setSelected] = useState(null); // { group, image } or null

  const { filteredGroups } = useMemo(() => {
    const ongoing = projects.filter((p) => p.section === 'ongoing');
    const past = projects.filter((p) => p.section === 'past');
    const ongoingGroups = groupProjectsByClient(ongoing);
    const pastGroups = groupProjectsByClient(past);
    const all = [...ongoingGroups, ...pastGroups];
    const filtered =
      activeFilter === 'All'
        ? all
        : all.filter((g) => g.projects.some((p) => getProjectCategories(p).includes(activeFilter)));
    return { filteredGroups: filtered };
  }, [activeFilter]);

  const group_ = selected?.group;

  return (
    <section id="projects-content">
      <Sheet open={!!group_} onOpenChange={(open) => !open && setSelected(null)}>
        <StaggerContainer staggerDelay={0.08} threshold={0.1}>
          <div className="mb-6">
            <h2 className="font-serif text-2xl text-[#1A1A1A] font-semibold mb-2">
              {activeFilter === 'All' ? 'All Projects' : `${activeFilter} Projects`}
            </h2>
            <p className="text-[#6B6B65] text-sm max-w-2xl">
              {activeFilter === 'All'
                ? 'Our ongoing and completed work across all categories. Click a client to view projects.'
                : `Clients and projects in the ${activeFilter} category. Click a client to view details.`}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredGroups.map((group, index) => {
              const totalCards = filteredGroups.length;
              const imageNum = totalCards <= 1 ? 1 : Math.min(14, Math.max(1, 1 + Math.round((index / (totalCards - 1)) * 13)));
              const cardImage = PROJECT_IMAGES[`project${imageNum}`] ?? PROJECT_PLACEHOLDER;
              return (
                <ClientProjectCard
                  key={group.clientKey}
                  group={group}
                  image={cardImage}
                  onOpen={(group, image) => setSelected({ group, image })}
                />
              );
            })}
          </div>
        </StaggerContainer>

        <SheetContent side="right" className="w-full sm:max-w-md overflow-hidden flex flex-col p-0 gap-0 bg-[#FAF8F2] border-[#E0DDD9] shadow-xl rounded-l-xl">
          {group_ && (
            <>
              {/* Close button - matches project tag style */}
              <SheetClose
                className="absolute right-4 top-4 z-20 flex h-9 w-9 items-center justify-center rounded-full bg-white/90 text-[#1A1A1A] hover:bg-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#8B7355] focus-visible:ring-offset-2"
                aria-label="Close"
              >
                <X className="h-4 w-4 stroke-[2.5]" />
              </SheetClose>
              {/* Accent line */}
              <div className="h-1 w-full bg-gradient-to-r from-[#8B7355] via-[#9A8468] to-transparent rounded-tl-xl shrink-0" />
              {/* Header image */}
              {selected?.image && (
                <div className="relative h-36 sm:h-40 w-full overflow-hidden shrink-0">
                  <img
                    src={selected.image}
                    alt={group_.clientName}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A]/70 via-transparent to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <p className="text-white/90 text-xs font-medium tracking-widest uppercase">{group_.projects.length === 1 ? '1 project' : `${group_.projects.length} projects`}</p>
                  </div>
                </div>
              )}
              {/* Header + body (scrollable) */}
              <div className="flex-1 flex flex-col min-h-0 overflow-y-auto p-6">
                <SheetHeader className="p-0 space-y-1">
                  <SheetTitle className="font-serif text-2xl text-[#1A1A1A] leading-tight">{group_.clientName}</SheetTitle>
                  {group_.location && (
                    <p className="text-[#8B7355] font-medium text-sm flex items-center gap-1.5">
                      <span className="inline-block w-1 h-1 rounded-full bg-[#8B7355]" />
                      {group_.location}
                    </p>
                  )}
                  <p className="text-[#8A8884] text-xs uppercase tracking-wider mt-1">{group_.years}</p>
                </SheetHeader>
                <div className="mt-6 pt-5 border-t border-[#E0DDD9]">
                  <p className="text-[#8B7355] text-xs font-semibold tracking-widest uppercase mb-4">Projects</p>
                  <ul className="space-y-5">
                    {group_.projects.map((project, idx) => (
                      <li key={project.id} className="relative pl-4 border-l-2 border-[#8B7355]/30 hover:border-[#8B7355]/60 transition-colors">
                        <span className="absolute -left-4 top-0 flex h-5 w-5 items-center justify-center rounded-full bg-[#8B7355]/20 text-[#8B7355] text-xs font-semibold">
                          {idx + 1}
                        </span>
                        <h4 className="font-serif text-[#1A1A1A] font-semibold pt-0.5">{project.title}</h4>
                        <p className="text-[#6B6B65] text-sm mt-1.5 leading-relaxed">{project.description}</p>
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          {(project.categories ?? (project.category ? [project.category] : [])).map((cat) => (
                            <span key={cat} className="px-2 py-0.5 rounded bg-[#E0DDD9]/80 text-[#6B6B65] text-xs font-medium uppercase tracking-wider">
                              {cat}
                            </span>
                          ))}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </section>
  );
};

// Team content (no header - header is above)
const TeamContent = () => (
  <section id="team-content" className="pb-12">
    {teamSections.map((section, sectionIndex) => (
      <ScrollReveal key={section.id} variant="fadeUp" threshold={0.1}>
        <div id={`team-${section.id}`} className={sectionIndex > 0 ? 'mt-10 lg:mt-12' : ''}>
          <div className="text-center mb-5">
            <span className="text-[#8B7355] text-xs font-semibold tracking-[0.2em] uppercase">{section.category}</span>
            <h2 className="font-serif text-[#1A1A1A] mt-2 font-semibold text-2xl md:text-3xl">{section.title}</h2>
            {section.description && (
              <p className="text-[#6B6B65] leading-relaxed mt-2 text-sm max-w-xl mx-auto">{section.description}</p>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {section.members.map((member) => (
              <div
                key={member.id}
                className="group w-full bg-white rounded-2xl overflow-hidden border border-[#E0DDD9] hover:border-[#8B7355]/40 hover:shadow-xl hover:shadow-[#8B7355]/10 hover:-translate-y-1 transition-all duration-300"
              >
                <div className="relative aspect-[4/5] overflow-hidden bg-[#F7FAFC]">
                  {member.image?.includes('via.placeholder') ? (
                    <div className="w-full h-full flex flex-col items-center justify-center gap-2 p-6">
                      <ImageOff className="w-16 h-16 text-[#8A8884]" strokeWidth={1.5} />
                      <span className="text-[#8A8884] text-sm text-center leading-tight">{member.name}</span>
                    </div>
                  ) : (
                    <>
                      <img src={member.image} alt={member.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A]/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    </>
                  )}
                  <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-[#8B7355]/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
                <div className="p-5">
                  <h3 className="font-serif text-xl text-[#1A1A1A] font-semibold group-hover:text-[#8B7355] transition-colors">{member.name}</h3>
                  <p className="text-[#8B7355] text-sm font-medium mt-1">{member.role}</p>
                  <p className="text-[#6B6B65] text-sm leading-relaxed mt-2 line-clamp-4">{member.bio}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </ScrollReveal>
    ))}
  </section>
);

export default WorkPage;
